import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { mergeMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { Tenant } from './tenants.service';
import { PaymentStatus } from './payments.service';
import { AuthorizeNetTransaction } from './desktop-payments.service';

export type DesCreditPurchase = {
  id: string;
  number: string;
  tenant_id: string;
  registration_id?: number;
  credits: number;
  completed: boolean;
  completed_at?: string;
  base_amount: number;
  tax_amount: number;
  total_amount: number;
  created_at: string;
  quickbooks_invoice_id?: string;
  quickbooks_payment_id?: string;
  status?: string;
};

export type DesCreditPurchaseDetailsResponse = {
  des_credit_purchase: DesCreditPurchase;
  tenant: Tenant;
};

export type DesCreditPurchasesResponse = {
  complete: DesCreditPurchase[];
};

export type DesFulfillmentStatus =
  | 'none'
  | 'pending'
  | 'avatax_committed'
  | 'credits_applied'
  | 'fulfilled'
  | 'failed';

export type DesPayment = {
  id: string;
  des_credit_purchase_id: string;
  completed: boolean;
  status: PaymentStatus;
  status_description: string;
  fulfillment_status: DesFulfillmentStatus;
  created_at: string;
  updated_at: string;
};

export type DesPaymentResponse = {
  des_payment: DesPayment;
  des_credit_purchase?: DesCreditPurchase;
};

export type DesPaymentDetails = {
  des_payment: DesPayment;
  payment_method: string;
  transactions?: AuthorizeNetTransaction[];
};

export type DesPaymentDetailsResponse = {
  des_payment_details: DesPaymentDetails;
};

@Injectable({
  providedIn: 'root',
})
export class DesCreditPurchasesService {
  private baseUrl = environment.serverUrl + '/des-credit-purchases';
  private paymentsUrl = environment.serverUrl + '/des-payments';

  constructor(
    private firebaseService: FirebaseService,
    private http: HttpClient
  ) {}

  resolveGuid(encryptedGuid: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ registration_id: number }>(
          `${this.baseUrl}/resolve-guid`,
          { guid: encryptedGuid },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  create(credits: number, tenantId?: string, registrationId?: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const body: {
          credits: number;
          tenant_id?: string;
          registration_id?: number;
        } = { credits };
        if (registrationId != null) {
          body.registration_id = registrationId;
        }
        if (tenantId) {
          body.tenant_id = tenantId;
        }
        return this.http.post<DesCreditPurchaseDetailsResponse>(
          `${this.baseUrl}/create`,
          body,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  getById(id: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesCreditPurchaseDetailsResponse>(
          `${this.baseUrl}/get/${id}`,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  getForCurrentTenant() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesCreditPurchasesResponse>(
          `${this.baseUrl}/for-current-tenant`,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  getForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesCreditPurchasesResponse>(
          `${this.baseUrl}/for-tenant/${tenantId}`,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  getPaymentDetails(purchaseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesPaymentDetailsResponse>(
          `${this.paymentsUrl}/get-for-purchase/${purchaseId}`,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  sendReceiptEmail(purchaseId: string, emails: string[]) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post(
          `${this.baseUrl}/send-receipt-email`,
          { purchase_id: purchaseId, emails },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }
}
